// Coherent sample dataset for NPSP Components mockup.
// Same personas appear across Donor, Household, Recurring, and Relationship pages.
// Dates are expressed relative to "today" so the mockup always looks fresh.

const today = new Date();
const daysAgo = (n) => {
  const d = new Date(today);
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const monthsAgo = (n) => {
  const d = new Date(today);
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
};

// ═══════════════════════════════════════════════════════════════════════════
// Contacts
// ═══════════════════════════════════════════════════════════════════════════

export const contacts = {
  eleanor: {
    id: '003000000001',
    firstName: 'Eleanor',
    lastName: 'Chen',
    preferredName: 'Ellie',
    pronouns: 'she/her',
    title: 'Senior Partner',
    email: 'ellen.chen@example.com',
    phone: '(415) 555-0123',
    mobile: '(415) 555-0145',
    city: 'Oakland',
    state: 'CA',
    avatarInitials: 'EC',
    avatarColor: '#7F8DE1',
    householdId: '001000000001',
    primaryAffiliationId: '001000000002',
    lifetimeGiving: 98400,
    largestGift: 25000,
    firstGiftDate: '2019-05-11',
    lastGiftDate: daysAgo(0),
    lastGiftAmount: 500,
    numberOfGifts: 38,
    softCreditTotal: 18900,
    engagementScore: 87,
    donorScore: 87,
    scoreBreakdown: {
      wealth: 92,
      engagement: 89,
      individual: 85,
      household: 78,
      inclination: 91,
    },
    movesStage: 'cult',
    statusChips: ['major', 'monthly', 'board', 'volunteer'],
    activeRecurringMonthly: 125,
    retentionYears: 6,
    source: { label: 'Spring Gala 2019', type: 'Event · referral' },
    tags: ['Arts', 'Climate', 'Board Member', 'LYBUNT'],
    commsPrefs: {
      email: true, mail: true, phone: true, sms: false,
    },
    solicitor: { name: 'Maya Patel', role: 'MGO', avatarInitials: 'MP', avatarColor: '#E27B7B' },
  },
  miguel: {
    id: '003000000002',
    firstName: 'Miguel',
    lastName: 'Rivera',
    preferredName: 'Miguel',
    pronouns: 'he/him',
    email: 'miguel.rivera@example.com',
    phone: '(415) 555-0124',
    city: 'Oakland',
    state: 'CA',
    avatarInitials: 'MR',
    avatarColor: '#06a59a',
    householdId: '001000000001',
    lifetimeGiving: 26100,
    largestGift: 5000,
    numberOfGifts: 9,
    firstGiftDate: '2021-03-04',
    lastGiftDate: daysAgo(45),
    lastGiftAmount: 250,
    engagementScore: 74,
    donorScore: 72,
    movesStage: 'cult',
    statusChips: ['monthly', 'volunteer'],
    activeRecurringMonthly: 50,
  },
  kai: {
    id: '003000000003',
    firstName: 'Kai',
    lastName: 'Chen-Rivera',
    pronouns: 'they/them',
    avatarInitials: 'KR',
    avatarColor: '#fe9339',
    householdId: '001000000001',
    age: 14,
    lifetimeGiving: 0,
    numberOfGifts: 0,
    statusChips: [],
  },
  sarah: {
    id: '003000000004',
    firstName: 'Sarah',
    lastName: 'Chen',
    avatarInitials: 'SC',
    avatarColor: '#9050e9',
    relationshipToEleanor: 'Sibling',
  },
  david: {
    id: '003000000005',
    firstName: 'David',
    lastName: 'Park',
    avatarInitials: 'DP',
    avatarColor: '#5aa9e6',
    relationshipToEleanor: 'Friend',
  },
  mayaPatel: {
    id: '003000000006',
    firstName: 'Maya',
    lastName: 'Patel',
    avatarInitials: 'MP',
    avatarColor: '#E27B7B',
    relationshipToEleanor: 'Solicitor',
  },
  // A lapsed donor for variety
  robert: {
    id: '003000000007',
    firstName: 'Robert',
    lastName: 'Ashford',
    avatarInitials: 'RA',
    avatarColor: '#6b7a8f',
    lifetimeGiving: 55000,
    largestGift: 15000,
    numberOfGifts: 12,
    lastGiftDate: monthsAgo(14),
    lastGiftAmount: 1000,
    engagementScore: 22,
    donorScore: 41,
    movesStage: 'qual',
    statusChips: ['lapsed', 'major'],
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Household Account
// ═══════════════════════════════════════════════════════════════════════════

export const household = {
  id: '001000000001',
  name: 'Chen-Rivera Household',
  recordType: 'Household',
  primaryContactId: contacts.eleanor.id,
  formalGreeting: 'Mr. & Mrs. Chen-Rivera',
  informalGreeting: 'Eleanor & Miguel',
  memberIds: [contacts.eleanor.id, contacts.miguel.id, contacts.kai.id],
  roles: {
    [contacts.eleanor.id]: 'Head of Household',
    [contacts.miguel.id]: 'Spouse',
    [contacts.kai.id]: 'Child',
  },
  householdLifetime: 124500,
  householdLargestGift: 25000,
  firstGiftDate: '2019-05-11',
  lastGiftDate: daysAgo(0),
  activeRecurringMonthly: 175,
  softCreditTotal: 18900,
  thisYearTotal: 8400,
  lastYearTotal: 15200,
  avgGiftAmount: 3276,
  numberOfGifts: 47,
  addresses: [
    {
      id: 'addr-1',
      street: '1247 Alpine Ave',
      city: 'Oakland',
      state: 'CA',
      postalCode: '94612',
      isDefault: true,
      seasonalRange: null,
      label: 'Primary residence',
    },
    {
      id: 'addr-2',
      street: '812 Lakeshore Blvd',
      city: 'South Lake Tahoe',
      state: 'CA',
      postalCode: '96150',
      isDefault: false,
      seasonalRange: { startMonth: 11, startDay: 15, endMonth: 3, endDay: 31 },
      label: 'Winter residence',
    },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// Organization Account (for Eleanor's affiliation)
// ═══════════════════════════════════════════════════════════════════════════

export const organizations = {
  acmeFoundation: {
    id: '001000000002',
    name: 'Acme Foundation',
    recordType: 'Organization',
    industry: 'Philanthropy',
    employees: 120,
    matchingGiftCompany: true,
    lifetimeGiving: 250000,
  },
  chenAssociates: {
    id: '001000000003',
    name: 'Chen Associates LLP',
    recordType: 'Organization',
    industry: 'Legal Services',
    employees: 45,
    matchingGiftCompany: false,
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Affiliations (Contact ↔ Organization)
// ═══════════════════════════════════════════════════════════════════════════

export const affiliations = [
  {
    id: 'aff-1',
    contactId: contacts.eleanor.id,
    organizationId: organizations.acmeFoundation.id,
    role: 'Board Chair',
    status: 'Current',
    startDate: '2022-01-15',
    primary: true,
  },
  {
    id: 'aff-2',
    contactId: contacts.eleanor.id,
    organizationId: organizations.chenAssociates.id,
    role: 'Senior Partner',
    status: 'Current',
    startDate: '2011-06-01',
    primary: false,
  },
  {
    id: 'aff-3',
    contactId: contacts.eleanor.id,
    organizationId: organizations.acmeFoundation.id,
    role: 'Treasurer',
    status: 'Former',
    startDate: '2019-01-15',
    endDate: '2021-12-31',
    primary: false,
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// Relationships (Contact ↔ Contact)
// ═══════════════════════════════════════════════════════════════════════════

export const relationships = [
  { id: 'rel-1', fromId: contacts.eleanor.id, toId: contacts.miguel.id, type: 'Spouse',  status: 'Current' },
  { id: 'rel-2', fromId: contacts.eleanor.id, toId: contacts.kai.id,    type: 'Parent',  status: 'Current' },
  { id: 'rel-3', fromId: contacts.eleanor.id, toId: contacts.sarah.id,  type: 'Sibling', status: 'Current' },
  { id: 'rel-4', fromId: contacts.eleanor.id, toId: contacts.david.id,  type: 'Friend',  status: 'Current' },
];

// ═══════════════════════════════════════════════════════════════════════════
// Recurring Donation (Eleanor's $125/mo, started Apr 2022)
// ═══════════════════════════════════════════════════════════════════════════

export const recurringDonation = {
  id: 'rd-001',
  name: "Eleanor Chen's Monthly Gift",
  contactId: contacts.eleanor.id,
  status: 'Active',
  amount: 125,
  period: 'Monthly',
  frequency: 1,
  dayOfMonth: 12,
  startDate: '2022-04-12',
  endDate: null,
  nextPaymentDate: daysAgo(-22), // ~22 days from now
  recurringType: 'Open',
  paymentMethod: { brand: 'Visa', last4: '4242', expMonth: 9, expYear: 2028 },
  campaignId: 'cmp-001',
  committedAnnualValue: 1500,
  lifetimeFromRecurring: 4125,
  onTimeStreak: 12,
  installments: [
    // Last 12 months: 10 paid + 2 upcoming
    { date: monthsAgo(11), amount: 125, status: 'Paid', method: 'Visa ••4242' },
    { date: monthsAgo(10), amount: 125, status: 'Paid', method: 'Visa ••4242' },
    { date: monthsAgo(9),  amount: 125, status: 'Paid', method: 'Visa ••4242' },
    { date: monthsAgo(8),  amount: 125, status: 'Paid', method: 'Visa ••4242' },
    { date: monthsAgo(7),  amount: 125, status: 'Paid', method: 'Visa ••4242' },
    { date: monthsAgo(6),  amount: 125, status: 'Paid', method: 'Visa ••4242' },
    { date: monthsAgo(5),  amount: 125, status: 'Paid', method: 'Visa ••4242' },
    { date: monthsAgo(4),  amount: 125, status: 'Paid', method: 'Visa ••4242' },
    { date: monthsAgo(3),  amount: 125, status: 'Paid', method: 'Visa ••4242' },
    { date: monthsAgo(2),  amount: 125, status: 'Paid', method: 'Visa ••4242' },
    { date: monthsAgo(1),  amount: 125, status: 'Paid', method: 'Visa ••4242' },
    { date: monthsAgo(0),  amount: 125, status: 'Scheduled', method: 'Visa ••4242' },
  ],
  allocations: [
    { id: 'alloc-1', gauId: 'gau-annual',  gauName: 'Annual Fund',     percent: 70, amount: 87.50 },
    { id: 'alloc-2', gauId: 'gau-schol',   gauName: 'Scholarship Fund', percent: 30, amount: 37.50 },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════
// GAUs
// ═══════════════════════════════════════════════════════════════════════════

export const gaus = [
  { id: 'gau-annual',  name: 'Annual Fund',         active: true, ytdAllocated: 342000 },
  { id: 'gau-schol',   name: 'Scholarship Fund',    active: true, ytdAllocated: 78500 },
  { id: 'gau-capital', name: 'Capital Campaign',    active: true, ytdAllocated: 125000 },
  { id: 'gau-emerg',   name: 'Emergency Response',  active: true, ytdAllocated: 45000 },
];

// ═══════════════════════════════════════════════════════════════════════════
// Campaigns
// ═══════════════════════════════════════════════════════════════════════════

export const campaigns = {
  springAnnual: {
    id: 'cmp-001',
    name: 'Spring Annual Fund 2026',
    type: 'Appeal',
    status: 'In Progress',
    goal: 500000,
    raised: 342000,
    startDate: daysAgo(90),
    endDate: daysAgo(-30),
    budgetedCost: 15000,
    actualCost: 9400,
    numberOfResponses: 847,
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Timeline events (for Eleanor, last ~90 days)
// ═══════════════════════════════════════════════════════════════════════════

export const timelineEvents = [
  { id: 't001', type: 'gift',   date: daysAgo(0),  title: '$500 gift · Annual Fund',       description: 'Credit card · Spring Annual Fund', actor: 'System', amount: 500 },
  { id: 't002', type: 'email',  date: daysAgo(0),  title: 'Opened "Spring Newsletter"',    description: 'From: Development team',            actor: 'Eleanor', auto: true },
  { id: 't003', type: 'email',  date: daysAgo(1),  title: 'Opened "Impact Report Q1"',     description: 'From: Maya Patel',                  actor: 'Eleanor', auto: true },
  { id: 't004', type: 'email',  date: daysAgo(1),  title: 'Clicked "Donate Now"',          description: 'Campaign: Spring Annual',           actor: 'Eleanor', auto: true },
  { id: 't005', type: 'email',  date: daysAgo(1),  title: 'Opened "Meet our new ED"',      description: 'From: Executive office',            actor: 'Eleanor', auto: true },
  { id: 't006', type: 'call',   date: daysAgo(3),  title: 'Call · 12 min',                 description: 'Discussed Scholarship Fund interest. Next step: send impact packet.', actor: 'Maya Patel' },
  { id: 't007', type: 'task',   date: daysAgo(5),  title: 'Task: Send impact packet',      description: 'Due in 2 days · Maya Patel',        actor: 'Maya Patel', status: 'Open' },
  { id: 't008', type: 'gift',   date: daysAgo(7),  title: '$125 recurring · Monthly gift', description: 'Visa ••4242',                       actor: 'System', amount: 125, auto: true },
  { id: 't009', type: 'event',  date: daysAgo(14), title: 'Attended Spring Gala',          description: 'Table sponsor · Bid $1,800 in silent auction', actor: 'Eleanor' },
  { id: 't010', type: 'note',   date: daysAgo(18), title: 'Note: Considering named gift',  description: 'Mentioned potential $50k scholarship endowment after retirement planning.', actor: 'Maya Patel' },
  { id: 't011', type: 'gift',   date: daysAgo(37), title: '$125 recurring · Monthly gift', description: 'Visa ••4242',                       actor: 'System', amount: 125, auto: true },
  { id: 't012', type: 'email',  date: daysAgo(42), title: 'Opened "Board meeting prep"',   description: 'From: Executive office',            actor: 'Eleanor', auto: true },
  { id: 't013', type: 'volunteer', date: daysAgo(55), title: 'Volunteered · Community Day',description: '6 hours · Garden crew',             actor: 'Eleanor' },
  { id: 't014', type: 'gift',   date: daysAgo(67), title: '$125 recurring · Monthly gift', description: 'Visa ••4242',                       actor: 'System', amount: 125, auto: true },
  { id: 't015', type: 'call',   date: daysAgo(72), title: 'Call · 8 min',                  description: 'Check-in · Eleanor shared family update.', actor: 'Maya Patel' },
  { id: 't016', type: 'system', date: daysAgo(80), title: 'Record merged',                 description: 'Merged duplicate contact (003-X1234)', actor: 'System', auto: true },
  { id: 't017', type: 'system', date: daysAgo(82), title: 'Address updated',               description: 'Primary residence updated by Maya Patel', actor: 'System', auto: true },
  { id: 't018', type: 'gift',   date: daysAgo(97), title: '$125 recurring · Monthly gift', description: 'Visa ••4242',                       actor: 'System', amount: 125, auto: true },
];

// ═══════════════════════════════════════════════════════════════════════════
// Next best action
// ═══════════════════════════════════════════════════════════════════════════

export const nextBestAction = {
  title: 'Schedule a thank-you call',
  rationale: 'Eleanor gave $500 to the Annual Fund today. Major donors who receive a thank-you call within 72 hours give 26% more over the next 12 months (sample of 412 donors).',
  primaryCta: { label: 'Log a call', action: 'log-call' },
  secondaryCta: { label: 'Snooze', action: 'snooze' },
  urgency: 'soon',
};

// ═══════════════════════════════════════════════════════════════════════════
// Soft credits
// ═══════════════════════════════════════════════════════════════════════════

export const softCredits = [
  { id: 'sc-1', contactId: contacts.eleanor.id, opportunityId: 'opp-9001', amount: 5000,  role: 'Matching',   date: monthsAgo(2), donorName: 'Acme Foundation' },
  { id: 'sc-2', contactId: contacts.eleanor.id, opportunityId: 'opp-9002', amount: 10000, role: 'Influencer', date: monthsAgo(7), donorName: 'David Park' },
  { id: 'sc-3', contactId: contacts.eleanor.id, opportunityId: 'opp-9003', amount: 3900,  role: 'Matching',   date: monthsAgo(12), donorName: 'Acme Foundation' },
];

// ═══════════════════════════════════════════════════════════════════════════
// Gift history (for giving heatmap + table)
// ═══════════════════════════════════════════════════════════════════════════

// Spreads 38 gifts over ~6 years, weighted toward recent + monthly recurring
const buildGiftHistory = () => {
  const gifts = [];
  // Monthly recurring: last 24 months
  for (let i = 0; i < 24; i++) {
    gifts.push({
      id: `g-rec-${i}`, date: monthsAgo(i), amount: 125, type: 'Recurring', campaign: 'Spring Annual Fund',
    });
  }
  // Major one-time gifts
  gifts.push({ id: 'g-500-now', date: daysAgo(0), amount: 500, type: 'One-time', campaign: 'Spring Annual Fund 2026' });
  gifts.push({ id: 'g-25k-2024', date: '2024-12-18', amount: 25000, type: 'Major Gift', campaign: 'Year-End Match 2024' });
  gifts.push({ id: 'g-5k-2024', date: '2024-06-04', amount: 5000, type: 'Event', campaign: 'Spring Gala 2024' });
  gifts.push({ id: 'g-10k-2023', date: '2023-12-12', amount: 10000, type: 'One-time', campaign: 'Year-End Appeal 2023' });
  gifts.push({ id: 'g-5k-2023', date: '2023-06-20', amount: 5000, type: 'Event', campaign: 'Spring Gala 2023' });
  gifts.push({ id: 'g-7.5k-2022', date: '2022-12-15', amount: 7500, type: 'One-time', campaign: 'Year-End Appeal 2022' });
  gifts.push({ id: 'g-5k-2022', date: '2022-06-11', amount: 5000, type: 'Event', campaign: 'Spring Gala 2022' });
  gifts.push({ id: 'g-3k-2021', date: '2021-12-08', amount: 3000, type: 'One-time', campaign: 'Year-End Appeal 2021' });
  gifts.push({ id: 'g-2.5k-2020', date: '2020-12-10', amount: 2500, type: 'One-time', campaign: 'Year-End Appeal 2020' });
  gifts.push({ id: 'g-1k-2019', date: '2019-05-11', amount: 1000, type: 'Event', campaign: 'Spring Gala 2019' });
  return gifts;
};

export const giftHistory = buildGiftHistory();

// ═══════════════════════════════════════════════════════════════════════════
// Exported combined dataset
// ═══════════════════════════════════════════════════════════════════════════

const sampleData = {
  contacts,
  household,
  organizations,
  affiliations,
  relationships,
  recurringDonation,
  gaus,
  campaigns,
  timelineEvents,
  nextBestAction,
  softCredits,
  giftHistory,
};

export default sampleData;
